import type { OperationFailure } from "@auto-pools/core";
import type { AuthState } from "../types";

let csrfToken: string | undefined;
const diagnosticCodes = [
  "PREVIEW_TIMING_INVALID",
  "PREVIEW_RESPONSE_INVALID",
  "REQUEST_NETWORK_FAILED",
] as const;
type DiagnosticCode = (typeof diagnosticCodes)[number];
let diagnosticWindow = 0,
  diagnosticInFlight = false;
const reportedCodes = new Set<DiagnosticCode>();

export function reportPreviewDiagnostic(code: DiagnosticCode, priorRequestId?: string) {
  if (!csrfToken || diagnosticInFlight || !diagnosticCodes.includes(code)) return;
  const now = performance.now();
  if (now - diagnosticWindow >= 60_000) {
    diagnosticWindow = now;
    reportedCodes.clear();
  }
  if (reportedCodes.has(code)) return;
  reportedCodes.add(code);
  diagnosticInFlight = true;
  try {
    void fetch("/api/diagnostics/client", {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "content-type": "application/json", "x-csrf-token": csrfToken },
      body: JSON.stringify({
        code,
        operation: "open-preview",
        ...(typeof priorRequestId === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(priorRequestId)
          ? { priorRequestId }
          : {}),
      }),
    })
      .catch(() => undefined)
      .finally(() => {
        diagnosticInFlight = false;
      });
  } catch {
    diagnosticInFlight = false;
  }
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly operationFailure?: OperationFailure,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function setCsrfToken(value?: string) {
  csrfToken = value;
}

/** Routes that verify the account password inside an existing session (wallet, keys, tokens). */
const PASSWORD_REENTRY_PATHS = [
  "/wallet/reveal",
  "/wallet/preview",
  "/wallet/import",
  "/spot/key/generate",
  "/spot/key/activate",
  "/auth/viewer-token",
];

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  sensitive?: boolean;
  /**
   * The route re-asks the account password: a 401 `AUTH_FAILED` there is usually a mistyped
   * password with the session intact, so the session is confirmed before signing out.
   */
  passwordReauthentication?: boolean;
  onResponse?: (response: Response) => void;
};

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method?.toUpperCase() ?? "GET";
  const headers = new Headers(options.headers);
  if (options.body !== undefined) headers.set("content-type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) {
    headers.set("x-csrf-token", csrfToken);
  }
  const { body, sensitive, passwordReauthentication, onResponse, ...requestOptions } = options;
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...requestOptions,
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
      headers,
      credentials: "same-origin",
      cache: sensitive ? "no-store" : options.cache,
    });
  } catch (reason) {
    if (path === "/positions/preview" && !options.signal?.aborted)
      reportPreviewDiagnostic("REQUEST_NETWORK_FAILED");
    throw reason;
  }
  if (!response.ok) {
    let error: { code: string; message: string; operationFailure?: OperationFailure } = {
      code: "request_failed",
      message: `Request failed (${String(response.status)})`,
    };
    try {
      const payload = (await response.json()) as { error?: { code?: string; message?: string } };
      if (payload.error?.code && payload.error.message) error = payload.error as typeof error;
    } catch {
      // The generic message deliberately does not expose an upstream response body.
    }
    if (options.signal?.aborted) throw new DOMException("Aborted", "AbortError");
    if (response.status === 401) {
      let stillAuthenticated = false;
      if (
        passwordReauthentication &&
        error.code === "AUTH_FAILED" &&
        PASSWORD_REENTRY_PATHS.includes(path)
      ) {
        try {
          stillAuthenticated = (
            await api<AuthState>("/auth/state", { sensitive: true, signal: options.signal })
          ).authenticated;
        } catch {
          /* Session failure still signs out. */
        }
      }
      if (options.signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (!stillAuthenticated) window.dispatchEvent(new Event("auto-pools:unauthorized"));
    }
    throw new ApiError(error.code, error.message, response.status, error.operationFailure);
  }
  if (response.status === 204) return undefined as T;
  onResponse?.(response);
  return (await response.json()) as T;
}

export const authApi = {
  state: (signal?: AbortSignal) => api<AuthState>("/auth/state", { signal, sensitive: true }),
  login: (username: string, password: string) =>
    api<{ csrfToken: string }>("/auth/login", {
      method: "POST",
      body: { username, password },
      sensitive: true,
    }),
  logout: async () => {
    await api<unknown>("/auth/logout", { method: "POST", sensitive: true });
  },
};
